import * as THREE from 'three';
import {getBoxDistance} from 'components/customer/RoomPlanner/helpers/calculations';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';

// Walls carry dimension-arrow and edge-line children, so setFromObject(wall)
// returns an AABB far past the visible surface. Use the geometry instead.
export const getWallSurfaceBox = (wall: THREE.Mesh): THREE.Box3 => {
    wall.updateMatrixWorld(true);
    if (!wall.geometry.boundingBox) wall.geometry.computeBoundingBox();
    return wall.geometry.boundingBox.clone().applyMatrix4(wall.matrixWorld);
};

export const WALL_ROTATION_PROXIMITY = 30;

export interface RoomInteriorBounds {
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
}

export const getRoomInteriorBounds = (
    wallGroup?: THREE.Object3D | null
): RoomInteriorBounds | null => {
    const walls = (wallGroup?.children ?? []).filter(
        (child): child is THREE.Mesh =>
            (child as THREE.Mesh).isMesh && Boolean(child.name)
    );

    if (!walls.length) return null;

    const outer = new THREE.Box3();
    const size = new THREE.Vector3();
    let thickness = Infinity;

    // Thinnest horizontal extent across all walls is the wall thickness.
    walls.forEach((wall) => {
        const surface = getWallSurfaceBox(wall);
        outer.union(surface);
        surface.getSize(size);
        thickness = Math.min(thickness, size.x, size.z);
    });

    if (outer.isEmpty() || !Number.isFinite(thickness) || thickness <= 0) {
        return null;
    }

    return {
        minX: outer.min.x + thickness,
        maxX: outer.max.x - thickness,
        minZ: outer.min.z + thickness,
        maxZ: outer.max.z - thickness,
    };
};

export const isFootprintInsideRoom = (
    footprint: THREE.Box3,
    bounds: RoomInteriorBounds,
    tolerance = 1
): boolean =>
    footprint.min.x >= bounds.minX - tolerance &&
    footprint.max.x <= bounds.maxX + tolerance &&
    footprint.min.z >= bounds.minZ - tolerance &&
    footprint.max.z <= bounds.maxZ + tolerance;

// getBoxDistance returns 0 where the boxes overlap, so max() requires both
// "close to the wall plane" and "within the wall's span".
export const wallGap = (group: THREE.Object3D, wall: THREE.Mesh): number => {
    const gap = getBoxDistance(
        new THREE.Box3().setFromObject(group),
        getWallSurfaceBox(wall)
    );

    return Math.max(gap.x, gap.z);
};

export const isGroupBesideWall = (
    group: THREE.Object3D,
    wall: THREE.Mesh,
    threshold = WALL_ROTATION_PROXIMITY
): boolean => wallGap(group, wall) <= threshold;

export const clampGroupInsideRoom = (
    group: THREE.Object3D,
    wallGroup?: THREE.Object3D | null,
    bounds = getRoomInteriorBounds(wallGroup)
): THREE.Vector3 | null => {
    if (!bounds) return null;

    group.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(group);

    // A shape wider than the room cannot satisfy both edges; pin to min.
    let shiftX = 0;
    if (box.min.x < bounds.minX) shiftX = bounds.minX - box.min.x;
    else if (box.max.x > bounds.maxX) shiftX = bounds.maxX - box.max.x;

    let shiftZ = 0;
    if (box.min.z < bounds.minZ) shiftZ = bounds.minZ - box.min.z;
    else if (box.max.z > bounds.maxZ) shiftZ = bounds.maxZ - box.max.z;

    if (!shiftX && !shiftZ) return null;

    group.position.x += shiftX;
    group.position.z += shiftZ;
    group.updateMatrixWorld(true);

    return new THREE.Vector3(shiftX, 0, shiftZ);
};

export type HorizontalAxis = 'x' | 'z';

// A wall's normal axis is its thin horizontal extent.
export const getWallNormalAxis = (wallBox: THREE.Box3): HorizontalAxis =>
    wallBox.max.x - wallBox.min.x < wallBox.max.z - wallBox.min.z ? 'x' : 'z';

export const axisEdges = (box: THREE.Box3, axis: HorizontalAxis) =>
    axis === 'x'
        ? {min: box.min.x, max: box.max.x}
        : {min: box.min.z, max: box.max.z};

export const axisVector = (axis: HorizontalAxis, amount: number) =>
    axis === 'x'
        ? new THREE.Vector3(amount, 0, 0)
        : new THREE.Vector3(0, 0, amount);

const moveOnAxis = (
    group: THREE.Object3D,
    axis: HorizontalAxis,
    amount: number
) => {
    group.position.add(axisVector(axis, amount));
    group.updateMatrixWorld(true);
};

// Anything under this is already resting on the face.
export const WALL_FLUSH_TOLERANCE = 1;

export const L_SHAPE_LEG_WALL = 'leftWidth2Wall';

export const L_SHAPE_ARM_WALL = 'rightWidth2Wall';

const NOTCH_EPSILON = 0.5;

// The L cut-out is the quadrant past both inner walls' room-facing faces, so it
// is not covered by the rectangle getRoomInteriorBounds returns. Same faces as
// the drag-time clamp in baseProductClamping.
export const clampGroupOutsideLShapeNotch = (
    group: THREE.Object3D,
    wallGroup?: THREE.Object3D | null,
    bounds = getRoomInteriorBounds(wallGroup)
): THREE.Vector3 | null => {
    const legWall = wallGroup?.getObjectByName(L_SHAPE_LEG_WALL) as THREE.Mesh;
    const armWall = wallGroup?.getObjectByName(L_SHAPE_ARM_WALL) as THREE.Mesh;

    // Rectangular rooms have no notch.
    if (!legWall || !armWall) return null;

    group.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(group);

    // Clearing either face puts the footprint on a leg; crossing both puts it
    // in the cut-out.
    const penetration = {
        x: box.max.x - getWallSurfaceBox(legWall).min.x,
        z: box.max.z - getWallSurfaceBox(armWall).min.z,
    };
    if (penetration.x <= NOTCH_EPSILON || penetration.z <= NOTCH_EPSILON) {
        return null;
    }

    // Shallower retreat first, but it is useless when the footprint no longer
    // fits that leg, so fall back to the deeper one rather than push through a
    // wall.
    const order: HorizontalAxis[] =
        penetration.x <= penetration.z ? ['x', 'z'] : ['z', 'x'];
    const retreatOn = (axis: HorizontalAxis) =>
        axis === 'x' ? penetration.x : penetration.z;
    const staysInRoom = (axis: HorizontalAxis) =>
        !bounds ||
        axisEdges(box, axis).min - retreatOn(axis) >=
            (axis === 'x' ? bounds.minX : bounds.minZ) - NOTCH_EPSILON;

    const axis = order.find(staysInRoom) ?? order[0];
    const shift = -retreatOn(axis);
    moveOnAxis(group, axis, shift);

    return axisVector(axis, shift);
};

// Rotation can leave a shape in a wall, in the cut-out, or both. Walls first
// because the notch retreat assumes the footprint starts inside the room, then
// walls again in case a footprint that fits nowhere was pushed back out.
export const clampGroupIntoRoomFootprint = (
    group: THREE.Object3D,
    wallGroup?: THREE.Object3D | null
): THREE.Vector3 | null => {
    const bounds = getRoomInteriorBounds(wallGroup);
    const total = new THREE.Vector3();
    const apply = (shift: THREE.Vector3 | null) => {
        if (shift) total.add(shift);
        return shift;
    };

    apply(clampGroupInsideRoom(group, wallGroup, bounds));
    if (apply(clampGroupOutsideLShapeNotch(group, wallGroup, bounds))) {
        apply(clampGroupInsideRoom(group, wallGroup, bounds));
    }

    return total.lengthSq() ? total : null;
};

// Nearest wall the group rests against, or null when it is out in the room. A
// footprint correction can relocate a shape onto a different wall than the one
// it was snapped to.
export const findAbuttingWall = (
    group: THREE.Object3D,
    wallGroup?: THREE.Object3D | null,
    threshold = WALL_ROTATION_PROXIMITY
): THREE.Mesh | null => {
    let nearest: THREE.Mesh | null = null;
    let nearestGap = threshold;

    (wallGroup?.children ?? []).forEach((child) => {
        const wall = child as THREE.Mesh;
        if (!wall.isMesh || !wall.geometry || !child.name) return;

        const gap = wallGap(group, wall);
        // Strictly nearer, so the first wall wins a tie.
        if (gap < nearestGap) {
            nearestGap = gap;
            nearest = wall;
        }
    });

    return nearest;
};

const isMountableWallName = (name: string): boolean =>
    name.startsWith('wall:') ||
    name === 'leftWidthWall' ||
    name === 'leftWidth2Wall' ||
    name === 'rightWidthWall' ||
    name === 'rightWidth2Wall' ||
    name === 'leftDepthWall' ||
    name === 'rightDepthWall';

/**
 * Walks from a raycast hit up to the named wall mesh used for mounting.
 * @param {THREE.Object3D} object - Hit object, often an edge/arrow child
 * @return {THREE.Mesh | null} The wall mesh, or null when the hit is not a wall
 */
export const resolveNamedWallFromObject = (
    object: THREE.Object3D
): THREE.Mesh | null => {
    let current: THREE.Object3D | null = object;
    while (current) {
        if (
            isMountableWallName(current.name) &&
            (current as THREE.Mesh).isMesh
        ) {
            return current as THREE.Mesh;
        }
        current = current.parent;
    }
    return null;
};

const isWallHiddenFromRaycast = (
    wall: THREE.Object3D,
    raycaster: THREE.Raycaster
): boolean => {
    let node: THREE.Object3D | null = wall;
    while (node) {
        if (!node.visible) {
            return true;
        }
        node = node.parent;
    }
    // Hidden walls are moved to layer 1 in useRoomLayout.onHide.
    return !wall.layers.test(raycaster.layers);
};

/**
 * Visible, non-hidden walls along the cursor ray, nearest first.
 * Skips hidden-wall children (edges/measurements stay on layer 0).
 * @param {THREE.Raycaster} raycaster - Cursor ray
 * @param {THREE.Object3D} wallGroup - Rectangular or L-shape wall group
 * @return {THREE.Mesh[]} Unique wall meshes the ray hits
 */
const getVisibleWallsAlongRay = (
    raycaster: THREE.Raycaster,
    wallGroup: THREE.Object3D
): THREE.Mesh[] => {
    const hits = raycaster.intersectObjects(wallGroup.children, true);
    const walls: THREE.Mesh[] = [];
    const seen = new Set<string>();
    for (const hit of hits) {
        const wall = resolveNamedWallFromObject(hit.object);
        if (!wall || isWallHiddenFromRaycast(wall, raycaster)) {
            continue;
        }
        if (seen.has(wall.uuid)) {
            continue;
        }
        seen.add(wall.uuid);
        walls.push(wall);
    }
    return walls;
};

/**
 * First visible, non-hidden wall under the cursor. When dragPosition is
 * given, prefers the hit whose surface is closest to that point so a ray
 * that merely grazes a side wall does not steal the drag.
 * @param {THREE.Raycaster} raycaster - Cursor ray
 * @param {THREE.Object3D} wallGroup - Rectangular or L-shape wall group
 * @param {THREE.Vector3} [dragPosition] - Mouse point on the drag plane
 * @return {THREE.Mesh | null} The hovered wall, or null
 */
export const getVisibleWallUnderCursor = (
    raycaster: THREE.Raycaster,
    wallGroup: THREE.Object3D,
    dragPosition?: THREE.Vector3
): THREE.Mesh | null => {
    const walls = getVisibleWallsAlongRay(raycaster, wallGroup);
    const [nearest, ...otherHits] = walls;
    if (!nearest) {
        return null;
    }
    if (!dragPosition || otherHits.length === 0) {
        return nearest;
    }

    let best = nearest;
    let bestDist = getWallSurfaceBox(best).distanceToPoint(dragPosition);
    for (const wall of otherHits) {
        const dist = getWallSurfaceBox(wall).distanceToPoint(dragPosition);
        if (dist < bestDist) {
            best = wall;
            bestDist = dist;
        }
    }
    return best;
};

// A ray graze can be a few units closer than the attached wall; require a
// clear gap so slight drags cannot flap between walls.
const UPPER_WALL_JUMP_MARGIN = 10;

/**
 * Whether an upper should leave its attached wall for the hovered one.
 * False when the mouse is still nearer the current wall (ray clipped
 * another wall, or the cursor is on the cabinet rather than the new wall).
 * @param {THREE.Mesh | null | undefined} currentWall - Wall the cabinet is on
 * @param {THREE.Mesh | null | undefined} targetWall - Wall under the cursor
 * @param {THREE.Vector3} dragPosition - Mouse point on the drag plane
 * @return {boolean} True when the cursor is clearly on a different wall
 */
export const shouldJumpUpperToWall = (
    currentWall: THREE.Mesh | null | undefined,
    targetWall: THREE.Mesh | null | undefined,
    dragPosition: THREE.Vector3
): boolean => {
    if (!targetWall) {
        return false;
    }
    if (!currentWall?.isMesh) {
        return true;
    }
    if (targetWall.name === currentWall.name) {
        return false;
    }

    const distTarget =
        getWallSurfaceBox(targetWall).distanceToPoint(dragPosition);
    const distCurrent =
        getWallSurfaceBox(currentWall).distanceToPoint(dragPosition);
    if (!Number.isFinite(distTarget) || !Number.isFinite(distCurrent)) {
        return false;
    }

    return distTarget + UPPER_WALL_JUMP_MARGIN < distCurrent;
};

// Closes a residual gap on the wall's normal axis, leaving the along-wall
// position alone. The closest pairing of faces is the one meant to touch.
export const flushGroupToWall = (
    group: THREE.Object3D,
    wall: THREE.Mesh
): THREE.Vector3 | null => {
    const wallBox = getWallSurfaceBox(wall);
    if (wallBox.isEmpty()) return null;

    group.updateMatrixWorld(true);
    const axis = getWallNormalAxis(wallBox);
    const wallEdge = axisEdges(wallBox, axis);
    const groupEdge = axisEdges(new THREE.Box3().setFromObject(group), axis);

    const delta =
        Math.abs(groupEdge.min - wallEdge.max) <=
        Math.abs(groupEdge.max - wallEdge.min)
            ? wallEdge.max - groupEdge.min
            : wallEdge.min - groupEdge.max;

    // Matrix math leaves ~1e-13 residue on an already flush footprint.
    if (Math.abs(delta) <= WALL_FLUSH_TOLERANCE) return null;

    moveOnAxis(group, axis, delta);

    return axisVector(axis, delta);
};

// currentWall is set by the drag-time raycaster, so it can be null; fall back
// to the scene's wall group the same way getWallBehindCabinet does.
export const getRoomWallGroup = (
    group: THREE.Object3D,
    scene?: THREE.Object3D | null
): THREE.Object3D | null => {
    const currentWall = group.userData.currentWall as THREE.Mesh | null;
    if (currentWall?.parent) return currentWall.parent;

    return (
        scene?.children.find(
            (child) =>
                child.name === ObjectNameMap.rectangularWall ||
                child.name === ObjectNameMap.lShapeWall
        ) ?? null
    );
};

// currentWall is a cached last-snapped reference that only updates on
// specific events (drag-time wall adoption, an upper wall jump) and is never
// cleared when a cabinet drifts away from it. Re-validate against the live
// position before trusting it for a rotation reflush, the same way
// settleShapeAfterRotation already does for shape products: only skip the
// search when it is still genuinely flush (WALL_FLUSH_TOLERANCE), not merely
// within the much looser drag-adoption radius. Otherwise find whatever wall
// it actually rests against now, or null.
export const resolveLiveWall = (
    group: THREE.Object3D,
    wall: THREE.Mesh | null,
    wallGroup?: THREE.Object3D | null
): THREE.Mesh | null => {
    if (!wall) return null;
    if (isGroupBesideWall(group, wall, WALL_FLUSH_TOLERANCE)) return wall;

    return findAbuttingWall(group, wallGroup ?? wall.parent);
};

// Rotation preserves the AABB centre, so a shape can swing into a wall or the
// cut-out. Correcting that relocates a shape whose rotated footprint no longer
// fits its wall (a 400-deep footprint cannot stay on a 300-deep leg), so it
// comes to rest against a different one: re-point currentWall and close the
// residual gap, or the NEXT rotation measures a gap past reflushAfterRotation's
// threshold, bails, and applies no wall correction at all.
export const settleShapeAfterRotation = (
    group: THREE.Object3D,
    wallGroup?: THREE.Object3D | null
) => {
    clampGroupIntoRoomFootprint(group, wallGroup);

    const snappedWall = group.userData.currentWall as THREE.Mesh | null;
    if (
        snappedWall &&
        isGroupBesideWall(group, snappedWall, WALL_FLUSH_TOLERANCE)
    ) {
        return;
    }

    const landedWall = findAbuttingWall(group, wallGroup);
    if (!landedWall) return;

    group.userData.currentWall = landedWall;
    flushGroupToWall(group, landedWall);
    // Flushing toward a front or inner wall face moves the shape deeper into
    // the room footprint, so re-assert it.
    clampGroupIntoRoomFootprint(group, wallGroup);
};

const MIN_OCCLUDABLE_CORRIDOR = 4;

const CORRIDOR_EDGE_EPSILON = 1;

// Proximity alone cannot reject a screened wall: a thin corner panel leaves a
// group parked behind it still within WALL_ROTATION_PROXIMITY of that wall.
export const isWallOccludedByProduct = (
    group: THREE.Object3D,
    wall: THREE.Mesh,
    products: THREE.Object3D[]
): boolean => {
    if (!products?.length) return false;

    const wallBox = getWallSurfaceBox(wall);
    const groupBox = new THREE.Box3().setFromObject(group);

    const normalAxis = getWallNormalAxis(wallBox);
    const acrossAxis = normalAxis === 'x' ? 'z' : 'x';
    const groupNormal = axisEdges(groupBox, normalAxis);
    const wallNormal = axisEdges(wallBox, normalAxis);

    let near: number;
    let far: number;
    if (groupNormal.min >= wallNormal.max) {
        near = wallNormal.max;
        far = groupNormal.min;
    } else if (groupNormal.max <= wallNormal.min) {
        near = groupNormal.max;
        far = wallNormal.min;
    } else {
        return false;
    }

    if (far - near < MIN_OCCLUDABLE_CORRIDOR) return false;

    const across = axisEdges(groupBox, acrossAxis);
    const acrossMin = across.min + CORRIDOR_EDGE_EPSILON;
    const acrossMax = across.max - CORRIDOR_EDGE_EPSILON;
    const lowY = groupBox.min.y + CORRIDOR_EDGE_EPSILON;
    const highY = groupBox.max.y - CORRIDOR_EDGE_EPSILON;

    const corridor =
        normalAxis === 'x'
            ? new THREE.Box3(
                  new THREE.Vector3(near, lowY, acrossMin),
                  new THREE.Vector3(far, highY, acrossMax)
              )
            : new THREE.Box3(
                  new THREE.Vector3(acrossMin, lowY, near),
                  new THREE.Vector3(acrossMax, highY, far)
              );

    if (corridor.isEmpty()) return false;

    const groupId = group.userData?.id as unknown;

    return products.some((product) => {
        if (product === group) return false;

        const productId = product.userData?.id as unknown;
        if (productId !== undefined && productId === groupId) return false;

        return new THREE.Box3().setFromObject(product).intersectsBox(corridor);
    });
};
